import { Button, Loader, Text, UnstyledButton } from "@mantine/core";
import { Pencil } from "lucide-react";
import { useState } from "react";

import type { AdCopy, AdCopyTextElements } from "../../../api/generated/model";
import promptError from "../../../ui/assets/prompt-error-placeholder.png";
import classes from "./ad-copies.module.css";
import { ElementGroup } from "./ElementGroup";
import { SECTIONS, sectionOf, structureHas, valuesOf, type TextKey } from "./elements";
import type { CopyPicks } from "./picks";

/** Live's words while a copy is written, and when it failed. */
export const COPY_GENERATING = "We’re generating your Ad copies for you. Please wait a moment…";
export const COPY_FAILED = "Failed to generate Ad copy";
export const INCLUDED_TITLE = "Included elements - from the source inspiration";
export const ADDITIONAL_TITLE = "Additional elements - to expand the image copy";

/** A copy that failed, as live's (`failed-copies`): its error picture over the words (spec decision 20). */
export function CopyFailed() {
  return (
    <div className={`${classes.state} ${classes.failed}`}>
      <img src={promptError} alt="" className={classes.failedPicture} />
      <Text className={classes.failedWords}>{COPY_FAILED}</Text>
    </div>
  );
}

export interface CopyViewProps {
  copy: AdCopy;
  /** The active tile's analysed text, or null: a Style, or a reference with no structure. */
  structure: AdCopyTextElements | null;
  /** The draft has references (not the Style alone): the included sections have a title. */
  hasReferences: boolean;
  picks: CopyPicks | undefined;
  onToggleAll: (key: TextKey, values: readonly string[]) => void;
  onToggleValue: (key: TextKey, value: string) => void;
  onPlacement: (key: TextKey, placement: string) => void;
  /** Live's Edit, once the copy is written. */
  onEdit: () => void;
}

/**
 * The chosen copy beside the list (live's `copies-settings` `view-mode`, captures advanced-234/12
 * to 17): its title and Edit, then its state, or its sections. With an
 * analysed reference, the sections it shares come first; the rest are cards that add theirs.
 * Mount it per copy and tile, as live's: the sections added from cards start afresh.
 */
export function CopyView(props: CopyViewProps) {
  const { copy, structure, hasReferences, picks } = props;
  const [added, setAdded] = useState<TextKey[]>([]);
  const has = (key: TextKey) => valuesOf(copy.text_elements, key).length > 0;
  // Live's `getExcludedTextElements`: what the copy has and the inspiration does not.
  const excluded =
    structure === null
      ? []
      : SECTIONS.map((section) => section.key).filter(
          (key) => has(key) && !structureHas(structure, key),
        );
  // A section with ticks among them shows by itself (live's `getSelectedExcludedKeys`).
  const ticked = excluded.filter((key) => (picks?.[key]?.values.length ?? 0) > 0);
  const extra = [...new Set([...ticked, ...added])];
  const included = SECTIONS.map((section) => section.key).filter(
    (key) =>
      has(key) && !extra.includes(key) && (structure === null || structureHas(structure, key)),
  );
  const shown = [...included, ...extra];
  return (
    <div className={classes.pane}>
      <div className={classes.paneHeader}>
        <Text className={classes.paneTitle}>{copy.name}</Text>
        <Button
          variant="default"
          leftSection={<Pencil size={20} strokeWidth={1.5} />}
          disabled={copy.status !== "completed"}
          onClick={props.onEdit}
        >
          Edit
        </Button>
      </div>
      <div className={classes.paneBody}>
        {copy.status === "in_progress" ? (
          <div className={classes.state}>
            <Loader size="md" aria-hidden />
            <Text>{COPY_GENERATING}</Text>
          </div>
        ) : null}
        {copy.status === "failed" ? <CopyFailed /> : null}
        {copy.status === "completed" ? (
          <>
            <div className={classes.titled}>
              {hasReferences && shown.length > 0 ? (
                <Text className={classes.sectionTitle}>{INCLUDED_TITLE}</Text>
              ) : null}
              <div>
                {shown.map((key) => (
                  <ElementGroup
                    key={key}
                    sectionKey={key}
                    values={valuesOf(copy.text_elements, key)}
                    picked={picks?.[key]}
                    added={added.includes(key)}
                    onToggleAll={() => props.onToggleAll(key, valuesOf(copy.text_elements, key))}
                    onToggleValue={(value) => props.onToggleValue(key, value)}
                    onPlacement={(placement) => props.onPlacement(key, placement)}
                  />
                ))}
              </div>
            </div>
            {excluded.length === 0 ? null : (
              <div className={classes.titled}>
                <Text className={classes.sectionTitle}>{ADDITIONAL_TITLE}</Text>
                <div className={classes.cards}>
                  {excluded.map((key) => {
                    const { label, icon: Icon } = sectionOf(key);
                    return (
                      <UnstyledButton
                        key={key}
                        className={classes.card}
                        aria-pressed={extra.includes(key)}
                        onClick={() =>
                          setAdded((current) =>
                            current.includes(key)
                              ? current.filter((each) => each !== key)
                              : [...current, key],
                          )
                        }
                      >
                        <Icon size={16} strokeWidth={1.5} aria-hidden />
                        {label}
                        <span className={classes.count}>
                          {valuesOf(copy.text_elements, key).length}
                        </span>
                      </UnstyledButton>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
